import React from 'react';
import { Home, TrendingUp, Shield, Calculator, ArrowRight, MapPin, Building2, Mountain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { trackEvent } from '../utils/analytics';

// Canadian city images
const CANADIAN_IMAGES = {
  heroBackground: 'https://images.pexels.com/photos/30731301/pexels-photo-30731301.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  toronto: 'https://images.pexels.com/photos/29735136/pexels-photo-29735136.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  vancouver: 'https://images.pexels.com/photos/29072584/pexels-photo-29072584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  winterHome: 'https://images.pexels.com/photos/30731986/pexels-photo-30731986.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  colorfulHomes: 'https://images.pexels.com/photos/32973442/pexels-photo-32973442.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  suburbanHome: 'https://images.unsplash.com/photo-1760312206373-8108814e4dc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBDYW5hZGlhbiUyMG5laWdoYm9yaG9vZCUyMGhvdXNlc3xlbnwwfHx8fDE3NzE2MjgyMTF8MA&ixlib=rb-4.1.0&q=85'
};

const LandingPage = () => {
  const navigate = useNavigate();
  
  const handleGetStarted = () => {
    trackEvent('get_started_click', { source: 'hero' });
    navigate('/login');
  };
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      
      {/* Hero Section with Canadian Winter Home */}
      <div className="relative overflow-hidden pt-16">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('${CANADIAN_IMAGES.heroBackground}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-6">
              <Home className="h-10 w-10 text-primary" />
              <h1 className="text-4xl md:text-6xl font-heading font-bold tracking-tight">
                My Mortgage <span className="text-primary">Canada</span>
              </h1>
            </div>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Master your mortgage renewal with data-driven insights, rate forecasts, 
              and intelligent recommendations tailored for Canadian homeowners.
            </p>
            <button
              data-testid="get-started-btn"
              onClick={handleGetStarted}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
            >
              Get Started Free
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Canadian Cities Section */}
      <div className="bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <p className="text-center text-sm text-muted-foreground mb-6">
            Trusted by homeowners across Canada
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Toronto</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Vancouver</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Calgary</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Montreal</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>Ottawa</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-3xl md:text-4xl font-heading font-semibold text-center mb-12">
          Everything You Need to <span className="text-primary">Save Thousands</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Feature 1 */}
          <div data-testid="feature-rates" className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all">
            <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-2">Live Rate Tracking</h3>
            <p className="text-muted-foreground">
              Real-time rates from major Canadian lenders including TD, RBC, BMO, and more. 
              Stay informed with Bank of Canada policy updates.
            </p>
          </div>
          
          {/* Feature 2 */}
          <div data-testid="feature-calculator" className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all">
            <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <Calculator className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-2">Smart Calculators</h3>
            <p className="text-muted-foreground">
              Payment calculators, HELOC estimators, and prepayment scenario modeling. 
              Understand your options with precision.
            </p>
          </div>
          
          {/* Feature 3 */}
          <div data-testid="feature-decision" className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all">
            <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold mb-2">Decision Engine</h3>
            <p className="text-muted-foreground">
              Get personalized recommendations on renewal timing, rate selection, 
              and refinancing strategies based on your situation.
            </p>
          </div>
        </div>
      </div>
      
      {/* Canadian Skylines Section */}
      <div className="bg-card border-y border-border py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-center mb-4">
            From Coast to <span className="text-primary">Coast</span>
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Whether you're buying your first home in Vancouver or renewing your mortgage in Toronto, 
            we've got you covered with local market insights.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Toronto Card */}
            <div className="relative rounded-2xl overflow-hidden group">
              <img 
                src={CANADIAN_IMAGES.toronto}
                alt="Toronto skyline with CN Tower"
                className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  <h3 className="text-xl font-heading font-semibold">Toronto & GTA</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Canada's largest housing market. Track rates from Big 5 banks and local lenders.
                </p>
              </div>
            </div>
            
            {/* Vancouver Card */}
            <div className="relative rounded-2xl overflow-hidden group">
              <img 
                src={CANADIAN_IMAGES.vancouver}
                alt="Vancouver skyline with mountains"
                className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Mountain className="h-5 w-5 text-primary" />
                  <h3 className="text-xl font-heading font-semibold">Vancouver & BC</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Navigate BC's unique market with tailored rate comparisons and HELOC insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <p className="text-4xl font-bold font-mono text-primary">$5,100</p>
            <p className="text-sm text-muted-foreground mt-1">Max Cash Back Available</p>
          </div>
          <div>
            <p className="text-4xl font-bold font-mono text-primary">6+</p>
            <p className="text-sm text-muted-foreground mt-1">Major Lenders Tracked</p>
          </div>
          <div>
            <p className="text-4xl font-bold font-mono text-primary">Live</p>
            <p className="text-sm text-muted-foreground mt-1">Rate Updates Daily</p>
          </div>
          <div>
            <p className="text-4xl font-bold font-mono text-primary">Free</p>
            <p className="text-sm text-muted-foreground mt-1">To Get Started</p>
          </div>
        </div>
      </div>
      
      {/* CTA Section with Canadian Home */}
      <div className="relative">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('${CANADIAN_IMAGES.winterHome}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
        
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4">
            Ready to Take Control of Your Mortgage?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join thousands of Canadian homeowners saving money on their mortgages.
          </p>
          <button
            data-testid="cta-get-started-btn"
            onClick={handleGetStarted}
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
          >
            Start Your Free Account
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              <span className="font-heading font-semibold">My Mortgage Canada</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a>
              <p className="text-sm text-muted-foreground">
                &copy; 2025 My Mortgage Canada. Empowering Canadian homeowners from coast to coast.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;